import React, { useCallback } from 'react';

import type { ILandingPageRenderData } from '@share/common-types';

import { LandingPageRenderer } from './layouts';

import './landing-page.css';
import styles from './styles.module.scss';

interface CertificateLandingPageProps {
  data: ILandingPageRenderData;
  paymentPath: string;
  certificatesPath: string;
  backLabel: string;
  onBackClick: () => void;
  onBuyClick: () => void;
}

export function CertificateLandingPage({
  data,
  paymentPath,
  certificatesPath,
  backLabel,
  onBackClick,
  onBuyClick,
}: CertificateLandingPageProps): React.ReactElement {
  const renderData: ILandingPageRenderData = {
    ...data,
    customCtaUrl: paymentPath,
    products: data.products ?? [],
    averageRetailPrice: data.averageRetailPrice ?? 0,
    savings: data.savings ?? 0,
  };

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest('a');
      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href) {
        return;
      }

      const isPaymentLink =
        href === paymentPath || href.endsWith(paymentPath) || href.includes('/payment');
      if (!isPaymentLink) {
        return;
      }

      event.preventDefault();
      onBuyClick();
    },
    [onBuyClick, paymentPath],
  );

  return (
    <div className={styles.page}>
      <div className={styles.backBar}>
        <a
          href={certificatesPath}
          className={styles.backLink}
          onClick={(event) => {
            event.preventDefault();
            onBackClick();
          }}
        >
          {backLabel}
        </a>
      </div>
      <div id="landing-page-root" className={styles.root} onClick={handleClick}>
        <LandingPageRenderer data={renderData} />
        {renderData.trackingPixelHtml ? (
          <div
            className={styles.tracking}
            aria-hidden
            dangerouslySetInnerHTML={{ __html: renderData.trackingPixelHtml }}
          />
        ) : null}
      </div>
    </div>
  );
}
